import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import {AutomationNode, NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import type {PreviewMember} from 'crqlar-react-common/dist/models/automation/flowPreview.model';
import {
    AutomationService,
    TriggerCandidate
} from 'crqlar-react-common/dist/services/automation/AutomationService';
import {useTranslation} from 'react-i18next';
import {useQuery} from 'react-query';

import {useMe} from '../../../common/useMe';
import {SingleSelect} from '../../../common/newSelect';
import {isEventTriggerNode, isFanOutNode} from '../config/nodeCategories';
import {defaultTestMemberId, matchesTestMember, testMemberChoice} from '../utils/testMembers';
import type {TestRunSubject} from '../utils/testRunState';
import {remindsNow} from '../utils/remindNow';

export interface TriggerRunRequest {
    triggerNode?: string;
    testRecipient: string;
    entityId?: string;
    /** The people ticked under "Test with"; absent when the flow offers no choice. */
    memberIds?: string[];
    /** The Audience / Team members step they come from. */
    memberNodeId?: string;
}

interface TriggerFlowDialogProps {
    open: boolean;
    onClose: () => void;
    onRun: (request: TriggerRunRequest, subject: TestRunSubject) => void;
    flowId: string;
    isRunning: boolean;
    /** The start request failed: said inside the dialog, which stays open. */
    startFailed?: boolean;
    triggerNodes?: AutomationNode[];
    /** The saved flow's steps; they decide whether "Test with" is offered. */
    flowNodes?: AutomationNode[];
}

export const TRIGGER_CANDIDATES_QUERY_KEY = 'automation-trigger-candidates';
export const TEST_MEMBERS_QUERY_KEY = 'automation-test-members';

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A stable empty list: `data ?? []` would hand the effect a new array every render. */
const NO_CANDIDATES: TriggerCandidate[] = [];
const NO_NODES: AutomationNode[] = [];
const NO_MEMBERS: PreviewMember[] = [];
const NO_IDS: string[] = [];

const SEARCH_ABOVE = 8;

/**
 * The header's test run, for any status: emails go to the entered address, pushes are held, survey links are previews.
 * "Test with" pre-ticks one person and never falls back to everyone — Start stays disabled while the list loads,
 * fails or is empty. The event-trigger candidate is best-effort; without one the run uses the stub payload.
 */
const TriggerFlowDialog: React.FC<TriggerFlowDialogProps> = ({
    open,
    onClose,
    onRun,
    flowId,
    isRunning,
    startFailed = false,
    triggerNodes = [],
    flowNodes = NO_NODES
}) => {
    const {t} = useTranslation();
    const {user} = useMe();

    const [selectedTrigger, setSelectedTrigger] = useState<string>(() => (triggerNodes.length > 1 ? triggerNodes[0].id : ''));
    const [testRecipient, setTestRecipient] = useState<string>(() => user?.email ?? '');
    /** null means the default pick (`defaultIds`). */
    const [chosenIds, setChosenIds] = useState<string[] | null>(null);
    const [search, setSearch] = useState('');
    /** Counts openings, so each one refetches the people — the flow may have been saved since. */
    const [session, setSession] = useState(0);
    const [shownOpen, setShownOpen] = useState(open);

    // Reset while rendering, not in an effect: the first render of an opening then queries under the new
    // session and never shows the previous opening's people.
    if (open !== shownOpen) {
        setShownOpen(open);
        if (open) {
            setTestRecipient(user?.email ?? '');
            setSelectedTrigger(triggerNodes.length > 1 ? triggerNodes[0].id : '');
            setChosenIds(null);
            setSearch('');
            setSession(current => current + 1);
        }
    }

    // A trigger dropped on empty canvas is a legitimate second root, so a flow can have several.
    const showTriggerPicker = triggerNodes.length > 1;
    const activeTrigger = showTriggerPicker
        ? triggerNodes.find(node => node.id === selectedTrigger) ?? triggerNodes[0]
        : triggerNodes[0];
    const eventTrigger = !!activeTrigger && isEventTriggerNode(activeTrigger.type);
    // "Remind now" (utils/remindNow): the test reminds the guest invited most recently.
    const reminderTest = remindsNow(activeTrigger?.type, triggerNodes.map(node => node.type));

    const {data} = useQuery({
        queryKey: [TRIGGER_CANDIDATES_QUERY_KEY, flowId],
        queryFn: () => AutomationService.getTriggerCandidates(flowId),
        enabled: open && !!flowId && eventTrigger,
        retry: false
    });
    const candidates = data ?? NO_CANDIDATES;
    // An event-trigger run carries the newest entry, as if it had just happened.
    const newest = eventTrigger ? candidates[0] : undefined;
    const candidatesLoaded = eventTrigger && data !== undefined;

    // Without an email step the field shows only for an account without an address: the API needs one.
    const sendsEmail = flowNodes.length === 0
        || flowNodes.some(node => node.type === NodeType.SEND_EMAIL || node.type === NodeType.SEND_TEAM_EMAIL);
    const sendsSurvey = flowNodes.some(node => node.type === NodeType.SURVEY_INVITE);
    const showAddressField = sendsEmail || !user?.email;

    const fanOutNodes = useMemo(() => flowNodes.filter(node => isFanOutNode(node.type)), [flowNodes]);
    const offersChoice = !!activeTrigger && !eventTrigger && fanOutNodes.length > 0;
    const audienceNode = fanOutNodes.find(node => node.type === NodeType.AUDIENCE);
    const audienceName: string | undefined = audienceNode
        ? (audienceNode.config?.audienceName || audienceNode.label || undefined)
        : undefined;

    const membersQuery = useQuery({
        queryKey: [TEST_MEMBERS_QUERY_KEY, flowId, activeTrigger?.id, session],
        queryFn: () => AutomationService.previewFlow(flowId, undefined, activeTrigger?.id),
        enabled: open && !!flowId && offersChoice,
        retry: false,
        refetchOnWindowFocus: false,
        cacheTime: 0
    });
    const choice = useMemo(() => testMemberChoice(membersQuery.data), [membersQuery.data]);
    const members = choice?.members ?? NO_MEMBERS;
    const team = choice ? choice.memberType === 'USER' : fanOutNodes.length > 0 && !audienceNode;

    const listState: 'none' | 'loading' | 'failed' | 'empty' | 'list' = !offersChoice
        ? 'none'
        : membersQuery.isError
            ? 'failed'
            : !membersQuery.data
                ? 'loading'
                // Right now the run does not reach its Audience / Team members step: nobody to choose.
                : !choice
                    ? 'none'
                    : members.length > 0
                        ? 'list'
                        // The step has people, but the preview listed none of them.
                        : (choice.total ?? 0) > 0 ? 'failed' : 'empty';

    useEffect(() => {
        if (!open || !user?.email) return;
        setTestRecipient(current => current || user.email!);
    }, [open, user?.email]);

    // Derived with the list, so the list never renders with nobody ticked.
    const defaultIds = useMemo(() => {
        const first = defaultTestMemberId(members, flowNodes);
        return first ? [first] : NO_IDS;
    }, [members, flowNodes]);
    const chosen = chosenIds ?? (listState === 'list' ? defaultIds : NO_IDS);
    const memberIds = listState === 'list' ? chosen : undefined;
    const visibleMembers = useMemo(
        () => members.filter(member => matchesTestMember(member, search)),
        [members, search]
    );

    const trimmedRecipient = testRecipient.trim();
    const recipientValid = EMAIL_SHAPE.test(trimmedRecipient);
    const recipientError = trimmedRecipient !== '' && !recipientValid;
    // The test address is one of the real guests or team members: that person gets every test email.
    const realRecipient = recipientValid
        ? members.find(member => member.email?.trim().toLowerCase() === trimmedRecipient.toLowerCase())
        : undefined;

    const choiceMade = listState === 'none' || (listState === 'list' && chosen.length > 0);
    const canRun = !isRunning && recipientValid && choiceMade;

    const handleRun = () => onRun({
        triggerNode: showTriggerPicker ? selectedTrigger : undefined,
        testRecipient: trimmedRecipient,
        entityId: newest?.id,
        memberIds,
        memberNodeId: memberIds ? choice?.nodeId : undefined
    }, {
        address: trimmedRecipient,
        memberCount: memberIds?.length,
        memberName: memberIds?.length === 1
            ? members.find(member => member.id === memberIds[0])?.name || undefined
            : undefined,
        team
    });

    const handleClose = () => {
        if (!isRunning) {
            onClose();
        }
    };

    const toggleMember = (id: string) => setChosenIds(current => {
        const ticked = current ?? defaultIds;
        return ticked.includes(id) ? ticked.filter(entry => entry !== id) : [...ticked, id];
    });

    const address = trimmedRecipient || t('the address below');
    const promise = team
        ? t('No team member receives anything. Every email goes to {{address}} instead, exactly as the team member would get it.', {address})
        : t('No guest receives anything. Every email goes to {{address}} instead, exactly as the guest would get it.', {address});

    const listSource = team
        ? t('Team members this automation reaches right now')
        : audienceName ? t('Guests in “{{name}}” right now', {name: audienceName}) : t('Guests this automation reaches right now');
    const listNote = team
        ? t('Their emails use the name of each ticked team member and go to {{address}}.', {address})
        : t('Their emails use the name and stay of each ticked guest and go to {{address}}.', {address});

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={'sm'}
            fullWidth
            PaperProps={{sx: {maxHeight: '90vh'}}}
        >
            <DialogTitle sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                    <Typography variant={'h6'} fontWeight={600} noWrap>
                        {t('Test run')}
                    </Typography>

                    <Tooltip title={t('Close')}>
                        <IconButton onClick={handleClose} size={'small'} aria-label={t('Close')}>
                            <CloseIcon/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <Stack gap={2.5} sx={{mt: 1}}>
                    <Typography variant={'body1'}>
                        {promise}
                    </Typography>

                    {showTriggerPicker && (
                        <SingleSelect
                            label={t('Start from')}
                            value={selectedTrigger}
                            options={triggerNodes.map(trigger => ({value: trigger.id, label: trigger.label || trigger.type}))}
                            onChange={value => {
                                setSelectedTrigger(value);
                                setChosenIds(null);
                                setSearch('');
                            }}
                            disabled={isRunning}
                            disableClearable
                        />
                    )}

                    {reminderTest && (
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {t('Remind now test hint')}
                        </Typography>
                    )}

                    {candidatesLoaded && (
                        <Stack gap={0.5}>
                            <Typography variant={'body2'} fontWeight={600}>
                                {t('Test with')}
                            </Typography>
                            <Typography variant={'body2'} color={'text.secondary'}>
                                {newest
                                    ? t('The newest entry: {{entity}}', {
                                        entity: [newest.label, newest.subtitle].filter(Boolean).join(' · ')
                                    })
                                    : t('There is no recent entry, so names and details stay empty.')}
                            </Typography>
                        </Stack>
                    )}

                    {listState !== 'none' && (
                        <Stack gap={1}>
                            <Box>
                                <Typography variant={'body2'} fontWeight={600}>
                                    {t('Run the test as')}
                                </Typography>
                                <Typography variant={'body2'} color={'text.secondary'}>
                                    {listSource}
                                </Typography>
                            </Box>

                            {listState === 'loading' && (
                                <Stack direction={'row'} alignItems={'center'} gap={1} role={'status'} sx={{py: 0.5}}>
                                    <CircularProgress size={16} thickness={5}/>
                                    <Typography variant={'body2'} color={'text.secondary'}>
                                        {audienceName && !team
                                            ? t('Reading the audience “{{name}}”…', {name: audienceName})
                                            : t('Working out who it would reach…')}
                                    </Typography>
                                </Stack>
                            )}

                            {listState === 'failed' && (
                                <Stack direction={'row'} alignItems={'center'} gap={1}>
                                    <Typography variant={'body2'} color={'error'}>
                                        {team ? t('The team members could not be loaded.') : t('The guests could not be loaded.')}
                                    </Typography>
                                    <Button
                                        size={'small'}
                                        onClick={() => {
                                            setChosenIds(null);
                                            void membersQuery.refetch();
                                        }}
                                    >
                                        {t('Retry')}
                                    </Button>
                                </Stack>
                            )}

                            {listState === 'empty' && (
                                <Typography variant={'body2'} color={'text.secondary'}>
                                    {audienceName && !team
                                        ? t('Nobody is in “{{name}}” right now, so there is nobody to test with.', {name: audienceName})
                                        : t('There is nobody to test with right now.')}
                                </Typography>
                            )}

                            {listState === 'list' && (
                                <>
                                    {members.length > SEARCH_ABOVE && (
                                        <TextField
                                            fullWidth
                                            size={'small'}
                                            placeholder={team ? t('Search team members') : t('Search guests')}
                                            value={search}
                                            onChange={event => setSearch(event.target.value)}
                                            disabled={isRunning}
                                            inputProps={{'aria-label': team ? t('Search team members') : t('Search guests')}}
                                        />
                                    )}

                                    <Box
                                        sx={{
                                            maxHeight: 264,
                                            overflowY: 'auto',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                    >
                                        {visibleMembers.map(member => (
                                            <Box
                                                key={member.id}
                                                component={'label'}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    pl: 0.5,
                                                    pr: 1.5,
                                                    py: 0.25,
                                                    cursor: isRunning ? 'default' : 'pointer',
                                                    '&:hover': {bgcolor: 'action.hover'},
                                                    '& + &': {borderTop: '1px solid', borderColor: 'divider'},
                                                }}
                                            >
                                                <Checkbox
                                                    size={'small'}
                                                    checked={chosen.includes(member.id!)}
                                                    onChange={() => toggleMember(member.id!)}
                                                    disabled={isRunning}
                                                />
                                                <Box sx={{minWidth: 0}}>
                                                    <Typography variant={'body1'} fontWeight={600} noWrap>
                                                        {member.name || member.email || (team ? t('Team member') : t('Guest'))}
                                                    </Typography>
                                                    <Typography
                                                        variant={'body2'}
                                                        color={member.email ? 'text.secondary' : 'text.disabled'}
                                                        noWrap
                                                    >
                                                        {member.email || t('No email address')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}

                                        {visibleMembers.length === 0 && (
                                            <Typography variant={'body2'} color={'text.secondary'} sx={{px: 1.5, py: 1}}>
                                                {t('No results for "{{query}}"', {query: search.trim()})}
                                            </Typography>
                                        )}
                                    </Box>
                                    {sendsEmail && (
                                        <Typography variant={'body2'} color={'text.secondary'}>
                                            {listNote}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Stack>
                    )}

                    {showAddressField && (
                        <TextField
                            fullWidth
                            required
                            size={'small'}
                            type={'email'}
                            label={t('Send all emails to')}
                            value={testRecipient}
                            onChange={event => setTestRecipient(event.target.value)}
                            error={recipientError}
                            // The first line already says what the address is for.
                            helperText={recipientError ? t('Enter a valid email address.') : undefined}
                            disabled={isRunning}
                        />
                    )}

                    {sendsSurvey && (
                        <Typography variant={'body2'} color={'text.secondary'} sx={{mt: -1.5}}>
                            {t('Survey links in these emails open the survey in Crqlar. No invitation is created.')}
                        </Typography>
                    )}

                    {showAddressField && realRecipient && (
                        <Alert severity={'warning'}>
                            {team
                                ? t('{{address}} belongs to {{name}}. This team member will receive every test email.', {address: trimmedRecipient, name: realRecipient.name || trimmedRecipient})
                                : t('{{address}} belongs to {{name}}. This guest will receive every test email.', {address: trimmedRecipient, name: realRecipient.name || trimmedRecipient})}
                        </Alert>
                    )}

                    {startFailed && (
                        <Typography variant={'body2'} color={'error'}>
                            {t('The test run could not be started. Please try again.')}
                        </Typography>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={isRunning}>
                    {t('Cancel')}
                </Button>

                <LoadingButton
                    variant={'contained'}
                    onClick={handleRun}
                    loading={isRunning}
                    disabled={!canRun}
                >
                    {memberIds && memberIds.length > 1
                        ? t(team ? 'Start test run with team members' : 'Start test run with guests', {count: memberIds.length})
                        : t('Start test run')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default TriggerFlowDialog;
